import React, { useEffect, useState } from 'react';
import { adminApi, couponApi } from '../../api';
import { Coupon } from '../../types';

const EMPTY_FORM = {
  code: '',
  name: '',
  discountType: 'FIXED' as 'FIXED' | 'PERCENTAGE',
  discountValue: '',
  minOrderAmount: '',
  maxDiscountAmount: '',
  expiresAt: '',
};

const AdminCoupons: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = (p = page) => {
    adminApi.getCoupons(p).then(r => {
      setCoupons(r.data.data.content);
      setTotalPages(r.data.data.totalPages);
    });
  };

  useEffect(() => { load(); }, [page]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.createCoupon({
        code: form.code,
        name: form.name,
        discountType: form.discountType,
        discountValue: Number(form.discountValue),
        minOrderAmount: form.minOrderAmount ? Number(form.minOrderAmount) : undefined,
        maxDiscountAmount: form.maxDiscountAmount ? Number(form.maxDiscountAmount) : undefined,
        expiresAt: form.expiresAt || undefined,
      });
      setForm(EMPTY_FORM);
      setShowForm(false);
      load();
    } catch (err: any) {
      alert(err.response?.data?.message || '쿠폰 생성 실패');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: number) => {
    await adminApi.toggleCoupon(id);
    load();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('쿠폰을 삭제하시겠습니까?')) return;
    await adminApi.deleteCoupon(id);
    load();
  };

  const handleIssue = async (couponId: number) => {
    const email = window.prompt('쿠폰을 지급할 고객 이메일을 입력하세요:');
    if (!email || !email.trim()) return;
    try {
      await couponApi.issueCouponByEmail(couponId, email.trim());
      alert(`쿠폰이 발급되었습니다: ${email.trim()}`);
    } catch (err: any) {
      alert(err.response?.data?.message || '발급 실패');
    }
  };

  const f = (field: keyof typeof form) => ({
    value: form[field],
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm({ ...form, [field]: e.target.value }),
  });

  return (
    <div>
      <div style={s.header}>
        <h2 style={s.title}>쿠폰 관리</h2>
        <button style={s.addBtn} onClick={() => setShowForm(!showForm)}>
          {showForm ? '닫기' : '+ 쿠폰 추가'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={s.form}>
          <h3 style={s.formTitle}>새 쿠폰 등록</h3>
          <div style={s.grid}>
            <div style={s.field}>
              <label style={s.label}>쿠폰 코드</label>
              <input style={s.input} placeholder="SUMMER20" required {...f('code')} />
            </div>
            <div style={s.field}>
              <label style={s.label}>쿠폰 이름</label>
              <input style={s.input} placeholder="여름 할인 쿠폰" required {...f('name')} />
            </div>
            <div style={s.field}>
              <label style={s.label}>할인 유형</label>
              <select style={s.input} {...f('discountType')}>
                <option value="FIXED">정액 ($)</option>
                <option value="PERCENTAGE">정률 (%)</option>
              </select>
            </div>
            <div style={s.field}>
              <label style={s.label}>
                할인 값 ({form.discountType === 'FIXED' ? '$' : '%'})
              </label>
              <input style={s.input} type="number" min="1" required {...f('discountValue')} />
            </div>
            <div style={s.field}>
              <label style={s.label}>최소 주문 금액 ($, 선택)</label>
              <input style={s.input} type="number" min="0" placeholder="없음" {...f('minOrderAmount')} />
            </div>
            <div style={s.field}>
              <label style={s.label}>최대 할인 금액 ($, 선택)</label>
              <input style={s.input} type="number" min="0" placeholder="없음" {...f('maxDiscountAmount')} />
            </div>
            <div style={s.field}>
              <label style={s.label}>만료일 (선택)</label>
              <input style={s.input} type="datetime-local" {...f('expiresAt')} />
            </div>
          </div>
          <div style={s.formActions}>
            <button type="submit" style={s.saveBtn} disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
            <button type="button" style={s.cancelBtn} onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}>
              취소
            </button>
          </div>
        </form>
      )}

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr style={s.thead}>
              <th style={s.th}>코드</th>
              <th style={s.th}>이름</th>
              <th style={s.th}>할인</th>
              <th style={s.th}>최소금액</th>
              <th style={s.th}>만료일</th>
              <th style={s.th}>상태</th>
              <th style={s.th}>관리</th>
            </tr>
          </thead>
          <tbody>
            {coupons.length === 0 ? (
              <tr><td colSpan={7} style={s.noData}>등록된 쿠폰이 없습니다.</td></tr>
            ) : (
              coupons.map(c => (
                <tr key={c.id} style={s.tr}>
                  <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: 700 }}>{c.code}</td>
                  <td style={s.td}>{c.name}</td>
                  <td style={s.td}>
                    {c.discountType === 'FIXED'
                      ? `$${c.discountValue.toLocaleString()}`
                      : `${c.discountValue}%`}
                    {c.maxDiscountAmount && (
                      <span style={s.subText}> (최대 ${c.maxDiscountAmount.toLocaleString()})</span>
                    )}
                  </td>
                  <td style={s.td}>
                    {c.minOrderAmount ? `$${c.minOrderAmount.toLocaleString()}` : '-'}
                  </td>
                  <td style={s.td}>
                    {c.expiresAt ? c.expiresAt.slice(0, 10) : '무기한'}
                  </td>
                  <td style={s.td}>
                    <span style={c.active ? s.activeBadge : s.inactiveBadge}>
                      {c.active ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <button style={s.issueBtn} onClick={() => handleIssue(c.id)}>발급</button>
                    <button style={s.toggleBtn} onClick={() => handleToggle(c.id)}>
                      {c.active ? '비활성화' : '활성화'}
                    </button>
                    <button style={s.deleteBtn} onClick={() => handleDelete(c.id)}>삭제</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={s.pagination}>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              style={{ ...s.pageBtn, ...(page === i ? s.pageBtnActive : {}) }}
              onClick={() => setPage(i)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700 },
  addBtn: { padding: '10px 20px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
  form: { background: '#fff', borderRadius: 12, padding: 24, marginBottom: 24, border: '1px solid #f0f0f0' },
  formTitle: { fontSize: 16, fontWeight: 700, marginBottom: 20 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 600, color: '#555' },
  input: { padding: '10px 12px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, outline: 'none' },
  formActions: { display: 'flex', gap: 12, marginTop: 20 },
  saveBtn: { padding: '10px 28px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' },
  cancelBtn: { padding: '10px 20px', background: '#f5f5f5', color: '#555', border: 'none', borderRadius: 8, cursor: 'pointer' },
  tableWrap: { background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #f0f0f0' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: '#fafafa' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#888', borderBottom: '1px solid #f0f0f0' },
  tr: { borderBottom: '1px solid #f8f8f8' },
  td: { padding: '14px 16px', fontSize: 13, color: '#333', verticalAlign: 'middle' },
  noData: { padding: '40px', textAlign: 'center', color: '#bbb', fontSize: 14 },
  subText: { fontSize: 11, color: '#aaa' },
  activeBadge: { display: 'inline-block', padding: '3px 10px', background: '#e8f5e9', color: '#4caf50', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  inactiveBadge: { display: 'inline-block', padding: '3px 10px', background: '#fafafa', color: '#bbb', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  issueBtn: { padding: '5px 12px', background: '#e8f5e9', color: '#2e7d32', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginRight: 8 },
  toggleBtn: { padding: '5px 12px', background: '#f0f0f0', color: '#555', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', marginRight: 8 },
  deleteBtn: { padding: '5px 12px', background: '#fff0f0', color: '#e53935', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' },
  pagination: { display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 },
  pageBtn: { width: 36, height: 36, border: '1px solid #e8e8e8', background: '#fff', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  pageBtnActive: { background: '#b39ddb', color: '#fff', border: 'none' },
};

export default AdminCoupons;
